import {
  addHours,
  addMinutes,
  getHours,
  isAfter,
  isBefore,
  set,
  startOfDay,
  startOfHour,
  startOfSecond,
} from 'date-fns'
import isNil from 'lodash-es/isNil'

import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import { createNowDateInUkTimezone } from '../../../utils/date'
import {
  APPOINTMENT_MOCK_REQUEST_NUMBER_PREFIX,
  APPOINTMENT_REMINDER_DEFAULT_MINUTES,
  AppointmentType,
} from '../Appointment.constants'

import {
  APPOINTMENT_FORM_DEFAULT_DURATION_MINUTES,
  APPOINTMENT_FORM_FIELD_NAME,
  APPOINTMENT_FORM_SPAN_ERROR_KEY,
  APPOINTMENT_FORM_TITLE_MAX_LENGTH,
} from './AppointmentFormModal.constants'
import {
  toAppointmentFormBuildingItem,
  toAppointmentFormFlatItem,
  toAppointmentFormRequestItem,
} from './AppointmentFormModal.utils.anchor'
import { getAppointmentFormMockParticipants } from './AppointmentFormModal.utils.participants'

function getAppointmentFormNextFullHour(timezone) {
  const now = startOfSecond(createNowDateInUkTimezone(timezone))

  const hour = startOfHour(now)

  return isBefore(hour, now) ? addHours(hour, 1) : hour
}

// An all-day pre-fill keeps the next full hour's time on its first day for a switch back to times
function getAppointmentFormInitialStart({
  firstDay,
  start,
  timezone,
  withEmptySpan,
}) {
  if (withEmptySpan) {
    return null
  }

  const nextFullHour = getAppointmentFormNextFullHour(timezone)

  const firstDayStart = isNil(firstDay)
    ? null
    : set(firstDay, { hours: getHours(nextFullHour) })

  return start ?? firstDayStart ?? nextFullHour
}

function getAppointmentFormDay(date) {
  return date ? startOfDay(date) : null
}

function toAppointmentFormMockAnchor(item) {
  return item ? { id: item.value, name: item.label } : null
}

function getAppointmentFormMockRequestNumber(request) {
  return isNil(request.number)
    ? null
    : `${APPOINTMENT_MOCK_REQUEST_NUMBER_PREFIX}${request.number}`
}

function toAppointmentFormMockRequest({ building, flat, request }) {
  return request
    ? {
        building,
        flat,
        number: getAppointmentFormMockRequestNumber(request),
        title: request.label,
      }
    : null
}

export function getAppointmentFormEnd(start) {
  return start
    ? addMinutes(start, APPOINTMENT_FORM_DEFAULT_DURATION_MINUTES)
    : null
}

export function getAppointmentFormInitialValues({
  building,
  emailThread = null,
  end,
  firstDay,
  lastDay,
  location = EMPTY_STRING,
  participants = EMPTY_LIST,
  request,
  resident = null,
  start,
  timezone,
  title = EMPTY_STRING,
  type = AppointmentType.general,
  withEmptySpan = false,
}) {
  const startValue = getAppointmentFormInitialStart({
    firstDay,
    start,
    timezone,
    withEmptySpan,
  })

  const firstDayValue = getAppointmentFormDay(firstDay ?? startValue)

  return {
    [APPOINTMENT_FORM_FIELD_NAME.building]: toAppointmentFormBuildingItem(
      request?.building ?? building
    ),
    [APPOINTMENT_FORM_FIELD_NAME.description]: EMPTY_STRING,
    [APPOINTMENT_FORM_FIELD_NAME.emailThread]: emailThread,
    [APPOINTMENT_FORM_FIELD_NAME.end]: end ?? getAppointmentFormEnd(startValue),
    [APPOINTMENT_FORM_FIELD_NAME.firstDay]: firstDayValue,
    [APPOINTMENT_FORM_FIELD_NAME.flat]: toAppointmentFormFlatItem(
      request?.flat
    ),
    [APPOINTMENT_FORM_FIELD_NAME.isAllDay]: !isNil(firstDay),
    [APPOINTMENT_FORM_FIELD_NAME.lastDay]: getAppointmentFormDay(
      lastDay ?? firstDayValue
    ),
    [APPOINTMENT_FORM_FIELD_NAME.location]: location,
    [APPOINTMENT_FORM_FIELD_NAME.participants]: participants,
    [APPOINTMENT_FORM_FIELD_NAME.reminder]:
      APPOINTMENT_REMINDER_DEFAULT_MINUTES,
    [APPOINTMENT_FORM_FIELD_NAME.request]: request
      ? toAppointmentFormRequestItem(request)
      : null,
    [APPOINTMENT_FORM_FIELD_NAME.resident]: resident,
    [APPOINTMENT_FORM_FIELD_NAME.start]: startValue,
    [APPOINTMENT_FORM_FIELD_NAME.title]: title.slice(
      0,
      APPOINTMENT_FORM_TITLE_MAX_LENGTH
    ),
    [APPOINTMENT_FORM_FIELD_NAME.type]: type,
  }
}

export function getAppointmentFormTranslatedOptions({ options, t }) {
  return options.map(option => ({ ...option, label: t(option.label) }))
}

export function getIsAppointmentFormSpanInvalid(values) {
  if (values[APPOINTMENT_FORM_FIELD_NAME.isAllDay]) {
    const firstDay = values[APPOINTMENT_FORM_FIELD_NAME.firstDay]
    const lastDay = values[APPOINTMENT_FORM_FIELD_NAME.lastDay]

    return !isNil(firstDay) && !isNil(lastDay) && isBefore(lastDay, firstDay)
  }

  const start = values[APPOINTMENT_FORM_FIELD_NAME.start]
  const end = values[APPOINTMENT_FORM_FIELD_NAME.end]

  return !isNil(start) && !isNil(end) && !isAfter(end, start)
}

export function getAppointmentFormSpanErrorKey(values) {
  return getIsAppointmentFormSpanInvalid(values)
    ? APPOINTMENT_FORM_SPAN_ERROR_KEY
    : null
}

export function getAppointmentFormDateErrorProps(spanErrorKey) {
  return error => ({ showError: Boolean(error || spanErrorKey) })
}

export function getIsAppointmentFormSubmittable(values) {
  const [spanStart, spanEnd] = values[APPOINTMENT_FORM_FIELD_NAME.isAllDay]
    ? [
        values[APPOINTMENT_FORM_FIELD_NAME.firstDay],
        values[APPOINTMENT_FORM_FIELD_NAME.lastDay],
      ]
    : [
        values[APPOINTMENT_FORM_FIELD_NAME.start],
        values[APPOINTMENT_FORM_FIELD_NAME.end],
      ]

  return (
    values[APPOINTMENT_FORM_FIELD_NAME.title].trim().length > 0 &&
    !isNil(spanStart) &&
    !isNil(spanEnd) &&
    !getIsAppointmentFormSpanInvalid(values)
  )
}

export function getAppointmentFormMockContext({ timezone, userData, values }) {
  const building = toAppointmentFormMockAnchor(
    values[APPOINTMENT_FORM_FIELD_NAME.building]
  )
  const flat = toAppointmentFormMockAnchor(
    values[APPOINTMENT_FORM_FIELD_NAME.flat]
  )
  const request = values[APPOINTMENT_FORM_FIELD_NAME.request]

  return {
    anchors: {
      building,
      emailThread: values[APPOINTMENT_FORM_FIELD_NAME.emailThread],
      flat,
      request: toAppointmentFormMockRequest({ building, flat, request }),
      resident: values[APPOINTMENT_FORM_FIELD_NAME.resident],
    },
    participants: getAppointmentFormMockParticipants({
      participants: values[APPOINTMENT_FORM_FIELD_NAME.participants],
      userData,
    }),
    timezone,
    user: userData && { id: userData.id, name: userData.fullname },
  }
}
